import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useNavigate } from 'react-router-dom';
import { Box, Text } from 'grommet';
import { Copy } from 'grommet-icons';
import { useAppTheme } from '@Components/Context';
import {
  Seo, InfoNotice, AppButton, SimpleModal,
} from '@Components/Control';
import { AdminNavWrapper } from '@Components/Navigation';
import {
  fetchAdminDemoLinks,
  createAdminDemoLink,
  updateAdminDemoLink,
  deleteAdminDemoLink,
} from '@Actions';
import {
  TableWrapper,
  NoDataPlaceholder,
  tablePadding,
  renderTableHeader,
} from '../../../Partial/Product/SharedComponents';
import AdminDemoCreateModal from './AdminDemoCreateModal';

const HEADER = [
  { display: 'Campaign', uid: 'name', size: '18%' },
  { display: 'Link URL', uid: 'url', size: '27%' },
  { display: 'Status', uid: 'status', size: '8%' },
  { display: 'Expires', uid: 'expires', size: '10%' },
  { display: 'Submissions', uid: 'submissions', size: '8%' },
  { display: '', uid: 'actions', size: '29%' },
];

function StatusBadge({ active, expired }) {
  let label;
  let bg;
  if (!active) { label = 'Inactive'; bg = '#6b7280'; } else if (expired) { label = 'Expired'; bg = '#dc2626'; } else { label = 'Active'; bg = '#16a34a'; }

  return (
    <Box
      round="2px"
      pad={{ vertical: '2px', horizontal: '6px' }}
      background={bg}
      style={{ display: 'inline-flex' }}
    >
      <Text size="0.72rem" color="#fff" weight={500}>{label}</Text>
    </Box>
  );
}

StatusBadge.propTypes = {
  active: PropTypes.bool.isRequired,
  expired: PropTypes.bool.isRequired,
};

const AdminDemoLinksPage = ({
  small,
  location,
  adminToken,
  fetchLinks,
  createLink,
  updateLink,
  deleteLink,
}) => {
  const { theme: { page, button }, isDarkMode } = useAppTheme();
  const usePageBg = isDarkMode ? page.background : page.altBg;
  const padHorizontal = small ? '1rem' : '2rem';

  const navigate = useNavigate();
  const [links, setLinks] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [showCreate, setShowCreate] = React.useState(false);
  const [generatedUrl, setGeneratedUrl] = React.useState(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [copied, setCopied] = React.useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = React.useState(null);

  const loadLinks = React.useCallback(() => {
    setLoading(true);
    fetchLinks(adminToken)
      .then((data) => setLinks(Array.isArray(data) ? data : []))
      .catch(() => setLinks([]))
      .finally(() => setLoading(false));
  }, [adminToken, fetchLinks]);

  React.useEffect(() => { loadLinks(); }, [loadLinks]);

  const handleCreate = React.useCallback(async (name, days) => {
    setSubmitting(true);
    await createLink(adminToken, days, name)
      .then((link) => {
        setGeneratedUrl(`${window.location.origin}/demo/${link.token}`);
        loadLinks();
      })
      .catch(() => {});
    setSubmitting(false);
  }, [adminToken, createLink, loadLinks]);

  const handleCloseCreate = React.useCallback(() => {
    setShowCreate(false);
    setGeneratedUrl(null);
  }, []);

  const handleToggleActive = React.useCallback((link) => {
    // eslint-disable-next-line no-underscore-dangle
    updateLink(adminToken, link._id, { isActive: !link.isActive }).then(loadLinks);
  }, [adminToken, updateLink, loadLinks]);

  const handleDeleteConfirmed = React.useCallback(() => {
    deleteLink(adminToken, confirmDeleteId).then(loadLinks);
    setConfirmDeleteId(null);
  }, [adminToken, confirmDeleteId, deleteLink, loadLinks]);

  const getDemoUrl = (token) => `${window.location.origin}/demo/${token}`;

  const handleCopy = (token) => {
    navigator.clipboard?.writeText(getDemoUrl(token));
    setCopied(token);
    setTimeout(() => setCopied(null), 2000);
  };

  const formatDate = (d) => (d
    ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' })
    : '—');

  const renderCell = (uid, link) => {
    const expired = new Date(link.expiresAt) <= new Date();

    if (uid === 'name') {
      return (
        <Text size="0.875rem" color={page.tableText} weight={500}>
          {link.name}
        </Text>
      );
    }

    if (uid === 'url') {
      return (
        <Box direction="row" gap="0.35rem" align="center">
          <Text
            size="0.75rem"
            color={page.secondaryText}
            style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}
          >
            {getDemoUrl(link.token)}
          </Text>
          <AppButton
            plain
            overrideHover
            level="bare"
            tip={copied === link.token ? 'Copied!' : 'Copy link'}
            onClick={() => handleCopy(link.token)}
            style={{ flexShrink: 0 }}
          >
            <Copy size="small" color={copied === link.token ? page.highlight : page.secondaryText} />
          </AppButton>
        </Box>
      );
    }

    if (uid === 'status') {
      return <StatusBadge active={link.isActive} expired={expired} />;
    }

    if (uid === 'expires') {
      return (
        <Text size="0.85rem" color={expired ? 'status-critical' : page.tableText}>
          {formatDate(link.expiresAt)}
        </Text>
      );
    }

    if (uid === 'accesses') {
      return (
        <Text size="0.85rem" color={page.tableText}>{link.accessCount ?? 0}</Text>
      );
    }

    if (uid === 'submissions') {
      const count = (link.submissions || []).length;
      return (
        <Text size="0.85rem" color={count > 0 ? page.tableText : page.secondaryText} style={{ whiteSpace: 'nowrap' }}>
          {count > 0 ? `${count} ${count === 1 ? 'person' : 'people'}` : 'None yet'}
        </Text>
      );
    }

    if (uid === 'actions') {
      const count = (link.submissions || []).length;
      return (
        <Box direction="row" gap="0.4rem" align="center" wrap={false}>
          {count > 0 && (
            <AppButton
              overrideHover
              level="tableControls"
              label="View Submissions"
              color={button.primaryText}
              bgColor={button.primaryBg}
              // eslint-disable-next-line no-underscore-dangle
              onClick={() => navigate(`/admin/demo-links/${link._id}/submissions`, { state: { link } })}
              style={{ whiteSpace: 'nowrap' }}
            />
          )}
          <AppButton
            overrideHover
            level="tableControls"
            label={link.isActive ? 'Deactivate' : 'Activate'}
            color={button.primaryText}
            bgColor={button.primaryBg}
            onClick={() => handleToggleActive(link)}
            style={{ whiteSpace: 'nowrap' }}
          />
          <AppButton
            overrideHover
            level="tableControls"
            label="Delete"
            color={button.primaryText}
            bgColor="#dc2626"
            // eslint-disable-next-line no-underscore-dangle
            onClick={() => setConfirmDeleteId(link._id)}
          />
        </Box>
      );
    }

    return null;
  };

  const renderContents = () => {
    if (loading || !links || links.length === 0) {
      return (
        <NoDataPlaceholder
          noShadow
          noRound
          loading={loading}
          label={loading ? 'Loading campaigns…' : 'No campaigns yet. Create one using the button above.'}
          textColor={page.primaryText}
          loaderColor={page.highlight}
        />
      );
    }

    return (
      <Box fill="horizontal" direction="column">
        {!small && (
          <Box
            fill="horizontal"
            wrap={false}
            direction="row"
            border={{
              color: page.tableBorder, size: '1px', style: 'solid', side: 'bottom',
            }}
          >
            {HEADER.map(({ uid, display, size }) => (
              <Box key={uid} width={size} pad={{ horizontal: '0.5rem', top: '1rem', bottom: '0.5rem' }}>
                {renderTableHeader(display, small)}
              </Box>
            ))}
          </Box>
        )}
        {links.map((link) => (
          <React.Fragment
            // eslint-disable-next-line no-underscore-dangle
            key={link._id}
          >
            <Box
              direction={small ? 'column' : 'row'}
              align={small ? 'start' : 'center'}
              border={{
                color: page.tableBorder, size: '1px', style: 'solid', side: 'bottom',
              }}
              pad={{ vertical: '0.75rem', right: '0.5rem' }}
              background={page.tableBg}
              fill="horizontal"
            >
              {HEADER.map(({ uid, display, size }) => (
                <Box
                  key={uid}
                  width={small ? '100%' : size}
                  pad={{ horizontal: small ? '0' : '0.5rem', vertical: small ? '0.25rem' : '0' }}
                  direction="row"
                  gap="0.5rem"
                >
                  {small && display && (
                    <Text size="0.875rem" color={page.tableHeader}>
                      {`${display}:`}
                    </Text>
                  )}
                  {renderCell(uid, link)}
                </Box>
              ))}
            </Box>
          </React.Fragment>
        ))}
      </Box>
    );
  };

  const tableControls = (
    <Box direction="row" gap="0.5rem" align="center">
      <AppButton
        overrideHover
        onClick={() => setShowCreate(true)}
        level="tableControls"
        height="var(--table-controls-height)"
        color={button.primaryText}
        bgColor={page.highlight}
        label="+ New Campaign"
      />
    </Box>
  );

  return (
    <AdminNavWrapper
      small={small}
      location={location}
      bannerProps={{
        title: 'Demo Campaigns',
        subTitle: 'Demo · Admin',
      }}
    >
      <Seo />
      <Box
        flex
        direction="column"
        background={usePageBg}
        pad={{ vertical: '1.5rem' }}
        height="100%"
        gap="1rem"
        width="100%"
      >
        <InfoNotice
          header="Campaign links"
          text="Generate shareable links for outreach campaigns. Each link can be used by multiple people — everyone fills in their details individually. Click on the submissions count to expand the list."
        />
        <Box pad={{ horizontal: padHorizontal }} width="100%">
          <TableWrapper
            small={small}
            header="Campaigns"
            boxPad={tablePadding(small)}
            isPaged={false}
            tableControls={tableControls}
          >
            <Box fill="horizontal" direction="column">
              {renderContents()}
            </Box>
          </TableWrapper>
        </Box>
      </Box>

      {showCreate && (
        <AdminDemoCreateModal
          small={small}
          onClose={handleCloseCreate}
          onSubmit={handleCreate}
          submitting={submitting}
          generatedUrl={generatedUrl}
        />
      )}

      {confirmDeleteId && (
        <SimpleModal
          small={small}
          toggleModal={(open) => { if (!open) setConfirmDeleteId(null); }}
          modalTitle="Delete Campaign"
          textColor={page.primaryText}
          highlightColor={page.highlight}
          modalBg={page.modalBg}
          hideLowerSection
          desktopMinWidth="26rem"
        >
          <Box direction="column" gap="1rem" pad={{ vertical: '1rem' }}>
            <Text size="0.875rem" color={page.primaryText}>
              Are you sure you want to delete this campaign? The link will stop working
              immediately and all submission records will be lost.
            </Text>
            <Box
              direction="row"
              justify="end"
              gap="0.5rem"
              border={{
                color: page.tableBorder, size: '1px', style: 'solid', side: 'top',
              }}
              pad={{ top: '1rem' }}
            >
              <AppButton
                overrideHover
                level="tableControls"
                label="Cancel"
                color={button.primaryText}
                bgColor={button.primaryBg}
                onClick={() => setConfirmDeleteId(null)}
              />
              <AppButton
                overrideHover
                level="tableControls"
                label="Delete"
                color={button.primaryText}
                bgColor="#dc2626"
                onClick={handleDeleteConfirmed}
              />
            </Box>
          </Box>
        </SimpleModal>
      )}
    </AdminNavWrapper>
  );
};

AdminDemoLinksPage.propTypes = {
  small: PropTypes.bool.isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
  }).isRequired,
  adminToken: PropTypes.string.isRequired,
  fetchLinks: PropTypes.func.isRequired,
  createLink: PropTypes.func.isRequired,
  updateLink: PropTypes.func.isRequired,
  deleteLink: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  adminToken: state.adminAuth?.adminToken || '',
});

const mapDispatchToProps = (dispatch) => bindActionCreators({
  fetchLinks: fetchAdminDemoLinks,
  createLink: createAdminDemoLink,
  updateLink: updateAdminDemoLink,
  deleteLink: deleteAdminDemoLink,
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(AdminDemoLinksPage);
